import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { AdminShell } from "@/components/AdminShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { adImageSrc } from "@/lib/image-src";
import {
  adminGetDispute,
  adminReplyDispute,
  adminResolveDispute,
} from "@/lib/orders-admin.functions";
import {
  RESOLUTIONS,
  disputeReasonLabel,
  disputeStatusLabel,
  disputeStatusTone,
  resolutionLabel,
  type DisputeResolution,
} from "@/lib/disputes";
import { deliveryLabel, kr, statusLabel } from "@/lib/orders";
import { DisputeEscalationPanel } from "@/components/DisputeEscalationPanel";

export const Route = createFileRoute("/_authenticated/admin/tvister_/$id")({
  component: AdminDispute,
  head: () => ({
    meta: [{ title: "Tvist – administration" }, { name: "robots", content: "noindex" }],
  }),
});

function AdminDispute() {
  const { id } = Route.useParams();
  const fetchDispute = useServerFn(adminGetDispute);
  const replyFn = useServerFn(adminReplyDispute);
  const resolveFn = useServerFn(adminResolveDispute);
  const qc = useQueryClient();

  const [reply, setReply] = useState("");
  const [internal, setInternal] = useState(false);
  const [resolution, setResolution] = useState<DisputeResolution>("release");
  const [refundAmount, setRefundAmount] = useState("");
  const [note, setNote] = useState("");
  const [internalNote, setInternalNote] = useState("");
  const [busy, setBusy] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-dispute", id],
    queryFn: () => fetchDispute({ data: { id } }),
    refetchInterval: 30_000,
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-dispute", id] });

  if (isLoading || !data) {
    return (
      <AdminShell title="Tvist">
        <p className="mt-6 text-muted-foreground">Laddar…</p>
      </AdminShell>
    );
  }

  const d = data.dispute as Record<string, any>;
  const o = (data.order ?? {}) as Record<string, any>;
  const posts = (data.posts ?? []) as Record<string, any>[];
  const open = d["status"] === "open";

  return (
    <AdminShell title={`Tvist på order #${o["order_no"] ?? "–"}`}>
      <Link to="/admin/tvister" className="mt-2 inline-flex items-center gap-2 text-sm text-muted-foreground">
        <ArrowLeft className="h-4 w-4" /> Till tvistkön
      </Link>

      <section className="mt-4 rounded-xl border border-border bg-card p-4">
        <div className="flex flex-wrap items-center gap-3">
          <span className={`rounded-full px-3 py-1 text-xs font-semibold ${disputeStatusTone(d["status"])}`}>
            {disputeStatusLabel(d["status"])}
          </span>
          <span className="font-semibold">{o["ad_title"]}</span>
          <span className="text-sm text-muted-foreground">
            {statusLabel(o["status"])} · {deliveryLabel(o["delivery_method"])}
          </span>
        </div>
        <dl className="mt-3 grid gap-1 text-sm sm:grid-cols-2">
          <div className="flex justify-between sm:block">
            <dt className="text-muted-foreground">Köparen betalade</dt>
            <dd className="font-medium">{kr(o["total_amount"] ?? 0)}</dd>
          </div>
          <div className="flex justify-between sm:block">
            <dt className="text-muted-foreground">Säljarens del</dt>
            <dd className="font-medium">{kr(o["payout_amount"] ?? 0)}</dd>
          </div>
          <div className="flex justify-between sm:block">
            <dt className="text-muted-foreground">Anledning</dt>
            <dd className="font-medium">{disputeReasonLabel(d["reason"])}</dd>
          </div>
          <div className="flex justify-between sm:block">
            <dt className="text-muted-foreground">Anmäld av</dt>
            <dd className="font-medium">{d["opened_by"] === "buyer" ? "Köparen" : "Säljaren"}</dd>
          </div>
        </dl>
        <Link
          to="/admin/ordrar/$id"
          params={{ id: o["id"] }}
          className="mt-3 inline-flex text-sm text-primary hover:underline"
        >
          Öppna ordern
        </Link>
      </section>

      {!open && (
        <section className="mt-4 rounded-xl border border-border bg-muted/40 p-4 text-sm">
          <p className="font-medium">Beslut: {resolutionLabel(d["resolution"])}</p>
          {Number(d["refund_amount"] ?? 0) > 0 && <p>Återbetalt: {kr(d["refund_amount"])}</p>}
          {Number(d["payout_amount"] ?? 0) > 0 && <p>Utbetalt till säljaren: {kr(d["payout_amount"])}</p>}
          {d["resolution_note"] && <p className="mt-1">{d["resolution_note"]}</p>}
        </section>
      )}

      <DisputeEscalationPanel
        dispute={d}
        escalations={(data.escalations ?? []) as Record<string, any>[]}
        onChanged={refresh}
      />

      <section className="mt-4 rounded-xl border border-border bg-card p-4">
        <h2 className="font-heading text-lg font-semibold">Samtal och bevis</h2>
        <ul className="mt-3 space-y-3">
          {posts.map((p) => (
            <li
              key={p["id"]}
              className={`rounded-lg border p-3 text-sm ${
                p["internal"] ? "border-amber-300 bg-amber-50/60" : "border-border"
              }`}
            >
              <p className="font-medium">
                {p["author_name"]}{" "}
                <span className="font-normal text-muted-foreground">
                  · {p["author_kind"]} · {new Date(p["created_at"]).toLocaleString("sv-SE")}
                  {p["internal"] && " · intern anteckning"}
                </span>
              </p>
              <p className="mt-1 whitespace-pre-wrap">{p["body"]}</p>
              {(p["attachments"] ?? []).length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {(p["attachments"] as string[]).map((a) => (
                    <a key={a} href={adImageSrc(a)} target="_blank" rel="noreferrer">
                      <img
                        src={adImageSrc(a)}
                        alt="Bevisbild"
                        className="h-24 w-24 rounded-md object-cover"
                        loading="lazy"
                      />
                    </a>
                  ))}
                </div>
              )}
            </li>
          ))}
        </ul>

        <div className="mt-4 space-y-2">
          <Textarea
            rows={3}
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            placeholder="Svara parterna, eller skriv en intern anteckning…"
          />
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" className="h-4 w-4 shrink-0 cursor-pointer accent-[hsl(var(--primary))]" checked={internal} onChange={(e) => setInternal(e.target.checked)} />
            Intern anteckning (syns bara för admin)
          </label>
          <Button
            disabled={busy || reply.trim().length < 2}
            onClick={async () => {
              setBusy(true);
              try {
                await replyFn({ data: { id, body: reply.trim(), internal } });
                setReply("");
                refresh();
              } catch (e) {
                toast.error((e as Error).message);
              } finally {
                setBusy(false);
              }
            }}
          >
            Skicka
          </Button>
        </div>
      </section>

      {open && (
        <section className="mt-4 rounded-xl border border-ocean-200 bg-ocean-50/60 p-4">
          <h2 className="font-heading text-lg font-semibold">Fatta beslut</h2>
          <div className="mt-3 grid gap-2">
            {RESOLUTIONS.map((r) => (
              <button
                key={r.value}
                type="button"
                onClick={() => setResolution(r.value)}
                className={`rounded-lg border p-3 text-left text-sm ${
                  resolution === r.value ? "border-primary bg-card" : "border-border bg-card/60 hover:bg-card"
                }`}
              >
                <span className="font-medium">{r.label}</span>
                <span className="block text-muted-foreground">{r.help}</span>
              </button>
            ))}
          </div>

          {resolution === "refund_partial" && (
            <div className="mt-3">
              <label className="text-sm font-medium">Belopp tillbaka till köparen (kr)</label>
              <Input
                type="number"
                min={1}
                max={Math.max(1, Number(o["total_amount"] ?? 0) - 1)}
                value={refundAmount}
                onChange={(e) => setRefundAmount(e.target.value)}
                className="mt-1 max-w-40"
              />
              <p className="mt-1 text-xs text-muted-foreground">
                Resten, upp till {kr(o["payout_amount"] ?? 0)}, betalas ut till säljaren.
              </p>
            </div>
          )}

          <Textarea
            rows={3}
            className="mt-3"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Motivering som skickas till båda parter…"
          />
          <Textarea
            rows={2}
            className="mt-2"
            value={internalNote}
            onChange={(e) => setInternalNote(e.target.value)}
            placeholder="Intern anteckning (valfri)"
          />

          <Button
            className="mt-3"
            disabled={
              busy ||
              note.trim().length < 5 ||
              (resolution === "refund_partial" && Number(refundAmount) < 1)
            }
            onClick={async () => {
              setBusy(true);
              try {
                const res = await resolveFn({
                  data: {
                    id,
                    resolution,
                    refundAmount: Number(refundAmount) || 0,
                    note: note.trim(),
                    internalNote: internalNote.trim(),
                  },
                });
                toast.success(
                  res.refund > 0
                    ? `Klart – ${res.refund} kr tillbaka till köparen.`
                    : "Klart – affären avslutad till säljarens fördel.",
                );
                setNote("");
                setInternalNote("");
                refresh();
              } catch (e) {
                toast.error((e as Error).message);
              } finally {
                setBusy(false);
              }
            }}
          >
            Bekräfta beslut
          </Button>
        </section>
      )}
    </AdminShell>
  );
}
